import { apiClient } from './client.js';
import type { ApiResponse } from '../types/index.js';

export interface StatTrendReason {
  label: string;
  delta: number;
  sourceType: 'project_daily' | 'project_completion' | 'natural' | 'planet_balance';
}

export interface StatTrend {
  trend: 'up' | 'down' | 'stable';
  netChange: number;
  reasons: StatTrendReason[];
}

export interface PlanetData {
  planet: {
    planet_id: string;
    owner_user_id: string;
    name: string;
    stage: number;
    current_day: number;
    created_at?: string;
    last_tick_at?: string;
    day_duration_ms?: number;
    home_star_index?: number | null;
    state: {
      stats: Record<string, number>;
      environmental: Record<string, number>;
      geography?: {
        landArea: number;
        waterCoverage: number;
        usableLand: number;
        usedLand: number;
      };
      life?: {
        lifeChance: number;
        lifeFactors: {
          waterBonus: number;
          tempBonus: number;
          chemBonus: number;
          atmosphereBonus: number;
        };
      };
    };
    visual_state: {
      stage: number;
      waterOpacity: number;
      atmosphereOpacity: number;
      vegetationOpacity: number;
      waterColor?: number;
      atmosphereColor?: number;
      starColor?: number;
    };
    surface_items: Array<{
      item_id: string;
      angle: number;
      altitude: number;
      layer: string;
      scale: number;
      visible?: boolean;
      upgrade?: {
        category: string;
        name: string;
      } | null;
      animationState?: Record<string, unknown>;
    }>;
  };
  balance: {
    current_balance: number;
    lifetime_earned: number;
    lifetime_spent: number;
  };
  statTrends?: Record<string, StatTrend>;
}

export const planetsApi = {
  getMe: () => apiClient.get<ApiResponse<PlanetData>>('/planets/me').then((r) => r.data),
  getById: (id: string) => apiClient.get(`/planets/${id}`).then((r) => r.data),
  purchase: (planetId: string, upgradeDefinitionId: string) =>
    apiClient
      .post<ApiResponse<{ planet: PlanetData['planet']; upgrade: unknown; balance: PlanetData['balance'] }>>(
        `/planets/${planetId}/purchase`,
        { upgradeDefinitionId }
      )
      .then((r) => r.data),
  emergencyBoost: (planetId: string, stat: string, amount: number) =>
    apiClient
      .post<ApiResponse<{ planet: PlanetData['planet']; stat: string; amount: number; cost: number; balance: PlanetData['balance'] }>>(
        `/planets/${planetId}/emergency`,
        { stat, amount }
      )
      .then((r) => r.data),
};
